import React from "react";
import { ActivityIndicator, StyleSheet, View } from "react-native";
import { useNavigate, useParams } from "react-router-dom";

import { SessionContext } from "../../../../contexts";
import {
  TClinicGalleryItem,
  getClinicGallery,
} from "../../../../services/api-clinic";
import {
  buildPhotoDeepLinkUrl,
  matchesPhotoImageId,
  resolvePhotoLinkTargetId,
} from "../../../../utils/photoShare";
import {
  GalleryModal,
  NormalText,
  PrimaryButton,
  TitleText,
} from "../../../common/native";

const PAGE_LIMIT = 200;
const MAX_PAGE_FETCHES = 200;

const tokens = {
  color: {
    text: "#0F172A",
    muted: "#475569",
    accent: "#00B7F4",
    background: "#FFFFFF",
    border: "#E2E8F0",
  },
  spacing: {
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
  },
  radius: {
    md: 12,
  },
  fontSize: {
    md: 16,
  },
  lineHeight: {
    md: 24,
  },
};

type PhotoLoadState = "loading" | "ready" | "error" | "not-found";

const PhotoDeepLinkPage: React.FC = () => {
  const session = React.useContext(SessionContext)?.session;
  const navigate = useNavigate();
  const { imageId: imageIdParam } = useParams();

  const [state, setState] = React.useState<PhotoLoadState>("loading");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [galleryItem, setGalleryItem] =
    React.useState<TClinicGalleryItem | null>(null);

  const normalizedImageId = React.useMemo(() => {
    const rawParam = imageIdParam || "";
    try {
      return decodeURIComponent(rawParam).trim();
    } catch {
      return rawParam.trim();
    }
  }, [imageIdParam]);

  const resolvedImageId = React.useMemo(
    () => resolvePhotoLinkTargetId(normalizedImageId),
    [normalizedImageId],
  );

  React.useEffect(() => {
    let isMounted = true;

    const loadPhoto = async () => {
      if (!session?.clinicId) {
        if (!isMounted) return;
        setState("error");
        setErrorMessage("You must be signed in to view this photo.");
        return;
      }

      if (!resolvedImageId) {
        if (!isMounted) return;
        setState("error");
        setErrorMessage("Invalid photo link.");
        return;
      }

      setState("loading");
      setErrorMessage("");
      setGalleryItem(null);

      try {
        let cursor: string | null | undefined = undefined;
        const seenCursors = new Set<string>();
        let foundItem: TClinicGalleryItem | null = null;
        let pageCount = 0;

        while (pageCount < MAX_PAGE_FETCHES) {
          pageCount += 1;
          if (cursor && seenCursors.has(cursor)) break;
          if (cursor) seenCursors.add(cursor);

          const response = await getClinicGallery(session, {
            cursor,
            limit: PAGE_LIMIT,
            mode: "chronological",
            include_patient_info: true,
          });

          foundItem =
            response.items.find((item) =>
              matchesPhotoImageId(item.image_id, resolvedImageId),
            ) || null;

          if (foundItem || !response.next_cursor) break;
          cursor = response.next_cursor;
        }

        if (!isMounted) return;

        if (!foundItem) {
          setState("not-found");
          return;
        }

        if (!foundItem.full_size_url && !foundItem.thumbnail_url) {
          setState("error");
          setErrorMessage("This photo is no longer available.");
          return;
        }

        setGalleryItem(foundItem);
        setState("ready");
      } catch {
        if (!isMounted) return;
        setState("error");
        setErrorMessage("Unable to load this photo. Please try again.");
      }
    };

    loadPhoto();

    return () => {
      isMounted = false;
    };
  }, [resolvedImageId, session]);

  if (state === "loading") {
    return (
      <View style={styles.centeredContainer}>
        <ActivityIndicator size="large" color={tokens.color.accent} />
        <NormalText
          text="Loading shared photo..."
          style={styles.helperText}
          numberOfLines={2}
        />
      </View>
    );
  }

  if (state === "error") {
    return (
      <View style={styles.centeredContainer}>
        <TitleText text="Unable to open photo" />
        <NormalText text={errorMessage} style={styles.helperText} />
        <PrimaryButton
          text="Open Photo Gallery"
          onPress={() => navigate("/photo-gallery")}
        />
      </View>
    );
  }

  if (state === "not-found" || !galleryItem) {
    return (
      <View style={styles.centeredContainer}>
        <TitleText text="Photo not found" />
        <NormalText
          text="This photo link is invalid or the image has been removed."
          style={styles.helperText}
        />
        <PrimaryButton
          text="Open Photo Gallery"
          onPress={() => navigate("/photo-gallery")}
        />
      </View>
    );
  }

  const photoUrl = galleryItem.full_size_url || galleryItem.thumbnail_url || "";
  const displayName =
    galleryItem.patient_name?.display_name ||
    `${galleryItem.patient_name?.first_name || ""} ${galleryItem.patient_name?.last_name || ""}`.trim();
  const modalTitle = displayName ? `${displayName} photo` : "Shared photo";
  const shareLink = buildPhotoDeepLinkUrl(
    galleryItem.image_id ?? resolvedImageId ?? "",
  );

  return (
    <View style={styles.readyContainer}>
      <View style={styles.readyCard}>
        <TitleText text="Shared photo" />
        <NormalText
          text="Close the viewer to return to your gallery."
          style={styles.helperText}
        />
      </View>
      <GalleryModal
        visible
        imageUrls={[photoUrl]}
        selectedIndex={0}
        onClose={() => navigate("/photo-gallery")}
        title={modalTitle}
        shareLinkForIndex={() => shareLink}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  centeredContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.color.background,
    gap: tokens.spacing.lg,
    paddingHorizontal: tokens.spacing.xl,
  },
  helperText: {
    color: tokens.color.muted,
    textAlign: "center",
    fontSize: tokens.fontSize.md,
    lineHeight: tokens.lineHeight.md,
    maxWidth: 480,
  },
  readyContainer: {
    flex: 1,
    backgroundColor: tokens.color.background,
  },
  readyCard: {
    marginBottom: tokens.spacing.md,
    borderWidth: 1,
    borderColor: tokens.color.border,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.lg,
  },
});

export default PhotoDeepLinkPage;
